import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Trophy,
  BookOpen,
  Headphones,
  Gamepad2,
  CreditCard,
  Bell,
  Palette,
  LineChart,
  Settings,
  LogOut,
  Target,
  Layers,
  FileText,
  Search,
} from 'lucide-react';
import ProgramSwitcher from './ProgramSwitcher';
import '../../styles/components/AdminLayout.css';

// Sidebar groups. Order and labels match the Phase 1 spec.
// `path: null` items are section headers (rendered as captions, not links).
const SIDEBAR_GROUPS = [
  {
    id: 'dashboard',
    items: [
      { path: '/admin/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    ],
  },
  {
    id: 'program',
    title: 'PROGRAMA',
    items: [
      { path: '/admin/programs', label: 'Programas', icon: <Layers size={20} /> },
      { path: '/admin/users', label: 'Usuários', icon: <Users size={20} /> },
    ],
  },
  {
    id: 'content',
    title: 'CONTEÚDO',
    items: [
      { path: '/admin/content', label: 'Conteúdo', icon: <BookOpen size={20} /> },
      { path: '/admin/blog', label: 'Blog', icon: <FileText size={20} /> },
      { path: '/admin/challenges', label: 'Desafios', icon: <Trophy size={20} /> },
      { path: '/admin/meditation', label: 'Meditação', icon: <Headphones size={20} /> },
    ],
  },
  {
    id: 'community',
    title: 'COMUNIDADE',
    items: [
      { path: '/admin/community', label: 'Comunidade', icon: <MessageSquare size={20} /> },
      { path: '/admin/gamification', label: 'Gamificação', icon: <Gamepad2 size={20} /> },
    ],
  },
  {
    id: 'comm',
    title: 'COMUNICAÇÃO',
    items: [
      { path: '/admin/notifications', label: 'Notificações', icon: <Bell size={20} /> },
    ],
  },
  {
    id: 'business',
    title: 'NEGÓCIOS',
    items: [
      { path: '/admin/subscriptions', label: 'Assinaturas', icon: <CreditCard size={20} /> },
      { path: '/admin/branding', label: 'Branding', icon: <Palette size={20} /> },
    ],
  },
  {
    id: 'analytics',
    title: 'ANALYTICS',
    items: [
      { path: '/admin/analytics', label: 'Analytics', icon: <LineChart size={20} /> },
      { path: '/admin/seo', label: 'SEO', icon: <Search size={20} /> },
      { path: '/admin/settings', label: 'Configurações', icon: <Settings size={20} /> },
    ],
  },
];

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAdminAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h1 className="sidebar-logo">
            <span className="logo-icon"><Target size={28} /></span>
            <span className="logo-text">App Admin</span>
          </h1>
          <ProgramSwitcher />
        </div>

        <nav className="sidebar-nav">
          {SIDEBAR_GROUPS.map((group) => (
            <div key={group.id} className="sidebar-group">
              {group.title && <div className="sidebar-group-title">{group.title}</div>}
              {group.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={'nav-item ' + (location.pathname === item.path ? 'active' : '')}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.avatar ? <img src={user.avatar} alt={user.name} /> : <span>{user?.name?.charAt(0) || 'A'}</span>}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.name}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="btn-logout">
            <LogOut size={18} />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-content">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
